import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild, HostListener, Optional } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { MatDatepickerModule, MatDateRangePicker } from '@angular/material/datepicker';
import moment from 'moment';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { ProjectService } from 'src/app/Services/project.service';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { SharedService } from 'src/app/Services/sharedService';
import { ConductTimelog, ConductTimelogGroup, ConductProjectSummary } from 'src/app/interface/conduct-timelog';
import { TimelineRenderedSession } from 'src/app/utils/timeline-session-logs.util';

const PROJECT_PALETTES = [
  { tagBg: 'bg-indigo-50', tagText: 'text-indigo-700', tagBorder: 'border-indigo-100', barHex: '#5459d1' },
  { tagBg: 'bg-emerald-50', tagText: 'text-emerald-700', tagBorder: 'border-emerald-200', barHex: '#10b981' },
  { tagBg: 'bg-amber-50', tagText: 'text-amber-800', tagBorder: 'border-amber-200', barHex: '#f59e0b' },
  { tagBg: 'bg-sky-50', tagText: 'text-sky-700', tagBorder: 'border-sky-200', barHex: '#0284c7' },
  { tagBg: 'bg-pink-50', tagText: 'text-pink-700', tagBorder: 'border-pink-200', barHex: '#ec4899' },
  { tagBg: 'bg-purple-50', tagText: 'text-purple-700', tagBorder: 'border-purple-200', barHex: '#8b5cf6' },
  { tagBg: 'bg-teal-50', tagText: 'text-teal-700', tagBorder: 'border-teal-200', barHex: '#0d9488' }
];

export interface ConductStatCard {
  icon: string;
  label: string;
  value: string;
  subtitle?: string;
  iconColor?: string;
  iconBgColor?: string;
  progress?: number;
  progressColor?: string;
}

@Component({
  selector: 'app-conduct-timelogs',
  templateUrl: './conduct-timelogs.component.html',
  styleUrls: ['./conduct-timelogs.component.css'],
  standalone: false,
})
export class ConductTimelogsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  /** Prevents fetchTimelogs() from being triggered on every URL param write-back. */
  private paramInitialized = false;

  // State properties
  loading = false;
  isConductIntegrated = true;
  userList: any[] = [];
  originalRawLogs: any[] = [];
  rawTimelogs: ConductTimelog[] = [];
  filteredTimelogs: ConductTimelog[] = [];
  groupedEmployeeTimelogs: ConductTimelogGroup[] = [];
  projectSummaries: ConductProjectSummary[] = [];
  availableProjects: Array<{ id: string; name: string }> = [];

  // Filter properties
  selectedProjectId = 'all';
  filterProjectName = '';
  selectedEmployeeId: string | number = '';
  searchQuery = '';
  activeView: 'employee' | 'project' = 'employee';

  // Pagination properties matching Employee List page
  pageIndex = 0;
  pageSize = 10;
  pageSizeOptions: number[] = [10, 25, 50, 100];
  employeeTrackedSecondsMap = new Map<string, number>();

  get pagedEmployeeTimelogs(): ConductTimelogGroup[] {
    if (!this.groupedEmployeeTimelogs) {
      return [];
    }
    const start = this.pageIndex * this.pageSize;
    return this.groupedEmployeeTimelogs.slice(start, start + this.pageSize);
  }

  onPaginateChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchTrackedTimeForPagedEmployees();
  }

  // Date selection state with presets and range picker
  startDate: Date = moment().startOf('day').toDate();
  endDate: Date = moment().endOf('day').toDate();
  minDate: Date = moment().subtract(1, 'year').startOf('day').toDate();
  maxDate: Date = moment().endOf('day').toDate();
  selectedFilterLabel = 'Today';
  selectedPreset = 'today';
  isOpenDateDropdown = false;

  selectedDateRange = new FormGroup({
    start: new FormControl<Date | null>(this.startDate),
    end: new FormControl<Date | null>(this.endDate)
  });

  presetFilters = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last7days' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Previous Month', value: 'previousMonth' }
  ];

  @ViewChild('conductRangePicker') conductRangePicker!: MatDateRangePicker<Date>;
  @ViewChild('dateRangeDropdown') dateRangeDropdown!: ElementRef;
  @ViewChild('dateRangeButton') dateRangeButton!: ElementRef;

  // Drawer properties
  isDrawerOpen = false;
  selectedDrawerGroup: ConductTimelogGroup | null = null;
  selectedDrawerGroupIndex = 0;
  selectedUserTimelineSessions: TimelineRenderedSession[] = [];

  // Computed KPI metrics
  totalTrackedHours = '00h 00m';
  totalDurationSeconds = 0;
  totalSuperSeeTrackedSeconds = 0;
  totalContributorsCount = 0;
  totalProjectsCount = 0;
  totalLogsCount = 0;

  private _cachedStartDate?: Date;
  private _cachedSelectedDate?: moment.Moment;

  get conductSelectedDate(): moment.Moment {
    if (this._cachedStartDate !== this.startDate || !this._cachedSelectedDate) {
      this._cachedStartDate = this.startDate;
      this._cachedSelectedDate = moment(this.startDate);
    }
    return this._cachedSelectedDate;
  }

  get conductDateModel(): Date {
    return this.startDate;
  }

  get isMultipleDates(): boolean {
    if (!this.startDate || !this.endDate) {
      return false;
    }
    return !moment(this.startDate).isSame(moment(this.endDate), 'day');
  }

  /**
   * Stable array — rebuilt only when KPI data changes (inside computeKpiMetrics).
   * NEVER use a getter here: a getter returns a new array on every change-detection
   * cycle which causes *ngFor to see a constant reference change and loop forever.
   */
  conductStatsCards: ConductStatCard[] = [
    {
      icon: 'schedule',
      label: 'Logged On Conduct',
      value: '00h 00m',
      subtitle: '',
      iconColor: 'text-[#5459d1]',
      iconBgColor: 'bg-indigo-50'
    },
    {
      icon: 'desktop_windows',
      label: 'Tracked By Supersee',
      value: '00h 00m',
      subtitle: 'Total desktop tracked time',
      iconColor: 'text-[#2172BC]',
      iconBgColor: 'bg-blue-50'
    },
    {
      icon: 'schedule',
      label: 'Unlogged time',
      value: '00h 00m',
      subtitle: 'Difference between tracked and logged time',
      iconColor: 'text-amber-500',
      iconBgColor: 'bg-amber-50'
    },
    {
      icon: 'speed',
      label: 'Match rate',
      value: '0%',
      subtitle: 'Logged time matched to tracked activity',
      iconColor: 'text-amber-500',
      iconBgColor: 'bg-amber-50',
      progress: 0,
      progressColor: 'bg-amber-500'
    },
    {
      icon: 'folder',
      label: 'Active project',
      value: '0',
      subtitle: 'Projects with logged time',
      iconColor: 'text-emerald-500',
      iconBgColor: 'bg-emerald-50'
    }
  ];

  constructor(
    private _projectService: ProjectService,
    private tokenEncryptionService: TokenEncryptionService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    @Optional() private _sharedService?: SharedService
  ) { }

  ngOnInit(): void {
    this.initUserList();
    this.listenToQueryParams();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Public Methods
  toggleDateDropdown(): void {
    this.isOpenDateDropdown = !this.isOpenDateDropdown;
  }

  closeDateDropdown(): void {
    this.isOpenDateDropdown = false;
  }

  formatDurationHhMm(seconds: number): string {
    const totalSecs = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(totalSecs / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m`;
  }

  parseHmsToSeconds(hms: unknown): number {
    if (!hms) return 0;
    if (typeof hms === 'number') return Math.max(0, Math.floor(hms));
    const str = String(hms).trim();
    if (!str || str === '-' || str === '--') return 0;
    const parts = str.split(':');
    if (parts.length >= 2) {
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      const s = parseInt(parts[2] || '0', 10) || 0;
      return h * 3600 + m * 60 + s;
    }
    const num = parseInt(str, 10);
    return isNaN(num) ? 0 : Math.max(0, num);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpenDateDropdown) {
      const clickedInsideDropdown = this.dateRangeDropdown?.nativeElement?.contains(event.target);
      const clickedButton = this.dateRangeButton?.nativeElement?.contains(event.target);
      const isDatepickerOverlay = (event.target as HTMLElement)?.closest('.cdk-overlay-container');

      if (!clickedInsideDropdown && !clickedButton && !isDatepickerOverlay) {
        this.isOpenDateDropdown = false;
      }
    }
  }

  setDatePreset(preset: { label: string; value: string }): void {
    const today = moment().startOf('day');
    let start: moment.Moment;
    let end: moment.Moment;

    switch (preset.value) {
      case 'today':
        start = today.clone();
        end = today.clone().endOf('day');
        break;
      case 'yesterday':
        start = today.clone().subtract(1, 'day').startOf('day');
        end = today.clone().subtract(1, 'day').endOf('day');
        break;
      case 'last7days':
        start = today.clone().subtract(6, 'days').startOf('day');
        end = today.clone().endOf('day');
        break;
      case 'thisMonth':
        start = today.clone().startOf('month');
        end = today.clone().endOf('day');
        break;
      case 'previousMonth':
        start = today.clone().subtract(1, 'month').startOf('month');
        end = today.clone().subtract(1, 'month').endOf('month');
        break;
      default:
        start = today.clone();
        end = today.clone().endOf('day');
    }

    this.startDate = start.toDate();
    this.endDate = end.toDate();
    this.selectedPreset = preset.value;
    this.selectedFilterLabel = preset.label;
    this.pageIndex = 0;

    this.selectedDateRange.patchValue(
      { start: this.startDate, end: this.endDate },
      { emitEvent: false }
    );

    this.closeDateDropdown();
    this.updateDateUrlParam();
    this.fetchTimelogs(true);
  }

  onDateRangePickerClosed(): void {
    const rangeVal = this.selectedDateRange.value;
    if (rangeVal.start && rangeVal.end) {
      this.startDate = moment(rangeVal.start).startOf('day').toDate();
      this.endDate = moment(rangeVal.end).endOf('day').toDate();
      this.selectedPreset = this.computePresetKey(this.startDate, this.endDate);
      this.selectedFilterLabel = this.formatRangeLabel(this.startDate, this.endDate);
      this.pageIndex = 0;
      this.closeDateDropdown();
      this.updateDateUrlParam();
      this.fetchTimelogs(true);
    }
  }

  onPreviousDate(): void {
    const isSingleDay = moment(this.startDate).isSame(moment(this.endDate), 'day');
    if (isSingleDay) {
      const prev = moment(this.startDate).subtract(1, 'day');
      this.startDate = prev.startOf('day').toDate();
      this.endDate = prev.endOf('day').toDate();
      this.selectedPreset = this.computePresetKey(this.startDate, this.endDate);
      this.selectedFilterLabel = this.formatRangeLabel(this.startDate, this.endDate);
    } else {
      const daysSpan = moment(this.endDate).diff(moment(this.startDate), 'days') + 1;
      const prevEnd = moment(this.startDate).subtract(1, 'day');
      const prevStart = prevEnd.clone().subtract(daysSpan - 1, 'days');
      this.startDate = prevStart.startOf('day').toDate();
      this.endDate = prevEnd.endOf('day').toDate();
      this.selectedPreset = 'custom';
      this.selectedFilterLabel = this.formatRangeLabel(this.startDate, this.endDate);
    }

    this.pageIndex = 0;
    this.selectedDateRange.patchValue({ start: this.startDate, end: this.endDate }, { emitEvent: false });
    this.updateDateUrlParam();
    this.fetchTimelogs(true);
  }

  onNextDate(): void {
    if (this.isNextDateDisabled()) {
      return;
    }
    const isSingleDay = moment(this.startDate).isSame(moment(this.endDate), 'day');
    if (isSingleDay) {
      const next = moment(this.startDate).add(1, 'day');
      this.startDate = next.startOf('day').toDate();
      this.endDate = next.endOf('day').toDate();
      this.selectedPreset = this.computePresetKey(this.startDate, this.endDate);
      this.selectedFilterLabel = this.formatRangeLabel(this.startDate, this.endDate);
    } else {
      const daysSpan = moment(this.endDate).diff(moment(this.startDate), 'days') + 1;
      const nextStart = moment(this.endDate).add(1, 'day');
      let nextEnd = nextStart.clone().add(daysSpan - 1, 'days');
      if (nextEnd.isAfter(moment().endOf('day'))) {
        nextEnd = moment().endOf('day');
      }
      this.startDate = nextStart.startOf('day').toDate();
      this.endDate = nextEnd.endOf('day').toDate();
      this.selectedPreset = 'custom';
      this.selectedFilterLabel = this.formatRangeLabel(this.startDate, this.endDate);
    }

    this.pageIndex = 0;
    this.selectedDateRange.patchValue({ start: this.startDate, end: this.endDate }, { emitEvent: false });
    this.updateDateUrlParam();
    this.fetchTimelogs(true);
  }

  isNextDateDisabled(): boolean {
    return moment(this.endDate).isSameOrAfter(moment(), 'day');
  }

  onDatePicked(pickedDate: Date | null): void {
    if (!pickedDate) {
      return;
    }
    this.startDate = moment(pickedDate).startOf('day').toDate();
    this.endDate = moment(pickedDate).endOf('day').toDate();
    this.selectedPreset = this.computePresetKey(this.startDate, this.endDate);
    this.selectedFilterLabel = this.formatRangeLabel(this.startDate, this.endDate);
    this.pageIndex = 0;
    this.selectedDateRange.patchValue({ start: this.startDate, end: this.endDate }, { emitEvent: false });
    this.updateDateUrlParam();
    this.fetchTimelogs(true);
  }

  private computePresetKey(start: Date, end: Date): string {
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');
    if (moment(start).isSame(today, 'day') && moment(end).isSame(today, 'day')) {
      return 'today';
    }
    if (moment(start).isSame(yesterday, 'day') && moment(end).isSame(yesterday, 'day')) {
      return 'yesterday';
    }
    return 'custom';
  }

  private formatRangeLabel(start: Date, end: Date): string {
    const s = moment(start);
    const e = moment(end);
    if (s.isSame(moment().startOf('day'), 'day') && e.isSame(moment().startOf('day'), 'day')) {
      return 'Today';
    }
    if (s.isSame(moment().subtract(1, 'day').startOf('day'), 'day') && e.isSame(moment().subtract(1, 'day').startOf('day'), 'day')) {
      return 'Yesterday';
    }
    if (s.isSame(e, 'day')) {
      return s.format('DD MMM YYYY');
    }
    return `${s.format('DD MMM YYYY')} - ${e.format('DD MMM YYYY')}`;
  }

  onProjectFilterChange(projectId: string): void {
    this.selectedProjectId = projectId;
    const found = this.availableProjects.find(p => p.id === projectId);
    this.filterProjectName = found && found.id !== 'all' ? found.name : '';
    this.pageIndex = 0;
    this.applyFilters();
  }

  clearProjectFilter(): void {
    this.selectedProjectId = 'all';
    this.filterProjectName = '';
    this.pageIndex = 0;
    this.applyFilters();
  }

  onEmployeeSelected(employeeId: string | number | any): void {
    if (employeeId && typeof employeeId === 'object') {
      this.selectedEmployeeId = employeeId.id !== undefined && employeeId.id !== null ? String(employeeId.id) : '';
    } else {
      this.selectedEmployeeId = employeeId !== null && employeeId !== undefined ? String(employeeId) : '';
    }
    this.pageIndex = 0;
    this.fetchSuperSeeTrackedTime();
    this.applyFilters();
  }

  onSearchChange(query?: string | number): void {
    if (query !== undefined) {
      this.searchQuery = String(query);
    }
    this.pageIndex = 0;
    this.applyFilters();
  }

  getSelectedProjectLabel(): string {
    if (!this.selectedProjectId || this.selectedProjectId === 'all') {
      return 'All Projects';
    }
    const found = this.availableProjects.find(p => p.id === this.selectedProjectId);
    return found ? found.name : 'All Projects';
  }

  hasActiveFilters(): boolean {
    return (
      (this.selectedProjectId !== 'all' && !!this.selectedProjectId) ||
      (this.selectedEmployeeId !== '' && this.selectedEmployeeId !== null && this.selectedEmployeeId !== undefined) ||
      (!!this.searchQuery && this.searchQuery.trim().length > 0) ||
      this.selectedPreset !== 'today'
    );
  }

  resetAllFilters(): void {
    this.selectedProjectId = 'all';
    this.filterProjectName = '';
    this.selectedEmployeeId = '';
    this.searchQuery = '';
    this.pageIndex = 0;
    this.totalSuperSeeTrackedSeconds = 0;
    this.setDatePreset({ label: 'Today', value: 'today' });
  }

  setActiveView(view: 'employee' | 'project'): void {
    this.activeView = view;
  }

  openDrawer(group: ConductTimelogGroup, index?: number): void {
    if (!group) {
      return;
    }
    this.selectedDrawerGroup = group;
    if (index !== undefined && index >= 0) {
      this.selectedDrawerGroupIndex = index;
    } else {
      const idx = this.groupedEmployeeTimelogs.findIndex(g => g.user_key === group.user_key);
      this.selectedDrawerGroupIndex = idx >= 0 ? idx : 0;
    }
    this.isDrawerOpen = true;

    this._sharedService?.componentDrawerSubject.next({
      componentName: 'conduct-timelogs',
      componentAction: 'open',
      componentWidth: 680,
      componentData: {
        group: this.selectedDrawerGroup,
        groups: this.groupedEmployeeTimelogs,
        currentIndex: this.selectedDrawerGroupIndex,
        selectedDate: this.startDate,
        startDate: this.startDate,
        endDate: this.endDate,
        users: this.userList,
        userTimelineSessions: this.selectedUserTimelineSessions
      }
    });
  }

  closeDrawer(): void {
    this.isDrawerOpen = false;
    this.selectedDrawerGroup = null;
    this.selectedUserTimelineSessions = [];
    this._sharedService?.componentDrawerSubject.next({
      componentName: 'conduct-timelogs',
      componentAction: 'close',
      componentData: null,
      componentWidth: 0
    });
  }

  onDrawerNavigate(index: number): void {
    if (index >= 0 && index < this.groupedEmployeeTimelogs.length) {
      this.selectedDrawerGroupIndex = index;
      this.selectedDrawerGroup = this.groupedEmployeeTimelogs[index];
    }
  }

  fetchTimelogs(isManual: boolean = false): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.fetchSuperSeeTrackedTime();

    const loggedInUser = this.tokenEncryptionService?.getEncryptedItem<any>('logedInUser') || {};
    const storedUser = this.tokenEncryptionService?.getEncryptedItem<any>('user') || {};
    const user = Object.keys(loggedInUser).length > 0 ? loggedInUser : storedUser;
    const companyId = loggedInUser?.company_id || storedUser?.company_id || user?.company_id || user?.id || user?.userId || 0;
    const formattedStartDate = moment(this.startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(this.endDate).format('YYYY-MM-DD');

    this._projectService.getConductIntegrationStatus(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (statusRes: any) => {
          if (!statusRes || !statusRes.is_integrated) {
            this.isConductIntegrated = false;
            this.loading = false;
            if (isManual) {
              Swal.fire('Integration Required', 'Conduct integration is not active. Please connect Conduct in Settings > Integrations.', 'info');
            }
            return;
          }

          this.isConductIntegrated = true;

          this._projectService.fetchConductTimelogs({
            supersee_company_id: companyId,
            requesting_user_id: user?.id,
            start_date: formattedStartDate,
            end_date: formattedEndDate
          })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res: any) => {
                this.loading = false;
                const raw = res?.timelogs || res?.data || (Array.isArray(res) ? res : []);
                if (raw && raw.length > 0) {
                  this.originalRawLogs = raw;
                  this.processIncomingLogs(raw);
                } else {
                  this.fetchFallbackLogs(companyId, formattedStartDate, formattedEndDate);
                }
              },
              error: () => {
                this.fetchFallbackLogs(companyId, formattedStartDate, formattedEndDate);
              }
            });
        },
        error: () => {
          this.loading = false;
          this.isConductIntegrated = false;
        }
      });
  }

  private fetchSuperSeeTrackedTime(): void {
    const loggedInUser = this.tokenEncryptionService?.getEncryptedItem<any>('logedInUser') || {};
    const storedUser = this.tokenEncryptionService?.getEncryptedItem<any>('user') || {};
    const user = Object.keys(loggedInUser).length > 0 ? loggedInUser : storedUser;
    const companyId = loggedInUser?.company_id || storedUser?.company_id || user?.company_id || user?.id || 0;
    const currentUserRoles = user?.role || user?.roles || loggedInUser?.role || loggedInUser?.roles || [];
    const role = Array.isArray(currentUserRoles)
      ? (currentUserRoles[0] === 'company' ? 'admin' : currentUserRoles[0])
      : (currentUserRoles === 'company' ? 'admin' : currentUserRoles);

    const formattedStartDate = moment(this.startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(this.endDate).format('YYYY-MM-DD');

    // 1. Fetch timesheet period summary (per-employee tracked / working times)
    if (this._projectService?.getTimesheetPeriodSummary) {
      this._projectService.getTimesheetPeriodSummary(formattedStartDate, formattedEndDate)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            const employees = res?.data?.employees || (Array.isArray(res?.employees) ? res.employees : []);
            if (employees && employees.length > 0) {
              employees.forEach((emp: any) => {
                const totalSecs = this.parseHmsToSeconds(emp.working || emp.total_working || emp.tracked_time)
                  || (this.parseHmsToSeconds(emp.productive) + this.parseHmsToSeconds(emp.idle));
                const empId = emp.user_id || emp.id || emp.employee_id;
                if (empId) {
                  this.employeeTrackedSecondsMap.set(String(empId), totalSecs);
                }
                if (emp.name) {
                  this.employeeTrackedSecondsMap.set(String(emp.name).toLowerCase().trim(), totalSecs);
                }
                if (emp.email) {
                  this.employeeTrackedSecondsMap.set(String(emp.email).toLowerCase().trim(), totalSecs);
                }
              });
            }
            if (this.selectedEmployeeId && this.selectedEmployeeId !== '') {
              const selectedSecs = this.employeeTrackedSecondsMap.get(String(this.selectedEmployeeId)) || 0;
              if (selectedSecs > 0) {
                this.totalSuperSeeTrackedSeconds = selectedSecs;
              }
            }
            this.enrichEmployeeTrackedTimes();
            this.computeKpiMetrics(this.filteredTimelogs);
            this.cdr.detectChanges();
          },
          error: (err: any) => {
            console.warn('[ConductTimelogs] Could not fetch timesheet period summary:', err);
          }
        });
    }

    // 2. Fetch usage summary for relevant employees
    const relevantEmpIds = Array.from(new Set(
      (this.filteredTimelogs || [])
        .map(l => l.supersee_user_id)
        .filter((id): id is string | number => id !== undefined && id !== null && id !== '')
    ));

    const payload: any = {
      company_id: companyId,
      user_id: user?.id,
      role: role || 'admin',
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      type: 'all'
    };

    if (this.selectedEmployeeId && this.selectedEmployeeId !== '') {
      payload.employee_ids = [this.selectedEmployeeId];
    } else if (relevantEmpIds.length > 0) {
      payload.employee_ids = relevantEmpIds;
    }

    if (this._projectService?.reportsUsageSummary) {
      this._projectService.reportsUsageSummary(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            const trackedSecs = res?.kpis?.total_time?.seconds ?? res?.total_time?.seconds ?? 0;
            const parsedSecs = parseInt(String(trackedSecs), 10) || 0;
            if (this.selectedEmployeeId && this.selectedEmployeeId !== '') {
              this.totalSuperSeeTrackedSeconds = parsedSecs;
              this.employeeTrackedSecondsMap.set(String(this.selectedEmployeeId), this.totalSuperSeeTrackedSeconds);
              this.enrichEmployeeTrackedTimes();
            } else if (!this.groupedEmployeeTimelogs || this.groupedEmployeeTimelogs.length === 0) {
              if (parsedSecs > 0) {
                this.totalSuperSeeTrackedSeconds = parsedSecs;
              }
            }
            this.computeKpiMetrics(this.filteredTimelogs);
            this.cdr.detectChanges();
          },
          error: (err: any) => {
            console.warn('[ConductTimelogs] Could not fetch SuperSee tracked summary:', err);
          }
        });
    }

    // 3. Also fetch reports matrix to populate all employee tracked times in bulk
    if (this._projectService?.reportsDataTableMatrix) {
      const matrixPayload: any = {
        ...payload,
        limit: 100,
        offset: 0
      };
      this._projectService.reportsDataTableMatrix(matrixPayload, false)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (matrixRes: any) => {
            const rows = matrixRes?.rows || (Array.isArray(matrixRes) ? matrixRes : []);
            if (rows && rows.length > 0) {
              rows.forEach((r: any) => {
                let totalSecs = 0;
                if (r.total_time?.seconds) {
                  totalSecs = parseInt(String(r.total_time.seconds), 10) || 0;
                } else if (r.usage && typeof r.usage === 'object') {
                  Object.values(r.usage).forEach((u: any) => {
                    totalSecs += parseInt(String(u?.seconds || 0), 10) || 0;
                  });
                }
                const empId = r.employee_id || r.user_id || r.id;
                if (empId) {
                  this.employeeTrackedSecondsMap.set(String(empId), totalSecs);
                }
                if (r.employee_name) {
                  this.employeeTrackedSecondsMap.set(String(r.employee_name).toLowerCase().trim(), totalSecs);
                }
              });
              this.enrichEmployeeTrackedTimes();
              this.cdr.detectChanges();
            }
            this.fetchTrackedTimeForPagedEmployees();
          },
          error: () => {
            this.fetchTrackedTimeForPagedEmployees();
          }
        });
    } else {
      this.fetchTrackedTimeForPagedEmployees();
    }
  }

  private initUserList(): void {
    const loggedInUser = this.tokenEncryptionService?.getEncryptedItem<any>('logedInUser') || {};
    const storedUser = this.tokenEncryptionService?.getEncryptedItem<any>('user') || {};
    const currentUser = Object.keys(loggedInUser).length > 0 ? loggedInUser : storedUser;

    const mergeUsers = (incoming: any): void => {
      const anyIncoming = incoming as any;
      const arr = Array.isArray(anyIncoming)
        ? anyIncoming
        : (anyIncoming?.data || anyIncoming?.users || anyIncoming?.body || []);
      if (!Array.isArray(arr) || arr.length === 0) return;

      const userMap = new Map<string, any>();
      // Preserve existing users
      this.userList.forEach(u => {
        if (!u) return;
        const idKey = String(u.id ?? u.ID ?? u.userId ?? '');
        if (idKey) userMap.set(idKey, u);
      });

      // Add incoming users
      arr.forEach((u: any) => {
        if (!u) return;
        const idKey = String(u.id ?? u.ID ?? u.userId ?? '');
        if (idKey) {
          userMap.set(idKey, { ...userMap.get(idKey), ...u });
        } else if (u.email || u.user_email) {
          userMap.set((u.email || u.user_email).toLowerCase().trim(), u);
        }
      });

      if (currentUser && (currentUser.id || currentUser.ID || currentUser.userId)) {
        const currId = String(currentUser.id || currentUser.ID || currentUser.userId);
        if (!userMap.has(currId)) {
          userMap.set(currId, currentUser);
        }
      }

      this.userList = Array.from(userMap.values());
      if (this._sharedService) {
        this._sharedService.userList = this.userList;
      }

      // Re-map timelogs whenever user list arrives or expands
      if (this.originalRawLogs.length > 0) {
        this.processIncomingLogs(this.originalRawLogs);
      }
    };

    // 1. Immediately read from sharedService cache if present
    if (this._sharedService?.userList && Array.isArray(this._sharedService.userList) && this._sharedService.userList.length > 0) {
      mergeUsers(this._sharedService.userList);
    }

    // 2. Immediately add current logged in user so admin/company user is resolved with 0ms delay
    if (currentUser && (currentUser.id || currentUser.ID || currentUser.userId)) {
      mergeUsers([currentUser]);
    }

    // 3. React to sharedService user list updates (e.g. background fetch completion on page reload)
    if (this._sharedService?.statusCheckTrigger) {
      this._sharedService.statusCheckTrigger
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (this._sharedService?.userList && Array.isArray(this._sharedService.userList) && this._sharedService.userList.length > 0) {
            mergeUsers(this._sharedService.userList);
          }
        });
    }

    // 4. Fetch main user list directly (independent, non-blocking)
    this._projectService.getUserList(0, 0, false)
      .pipe(takeUntil(this.destroy$), catchError(() => of([])))
      .subscribe((users: any) => {
        mergeUsers(users);
      });

    // 5. Also fetch non-employee roles independently (company owners, managers, leads)
    if (this._projectService?.getFilteredTeam) {
      this._projectService.getFilteredTeam('company')
        .pipe(takeUntil(this.destroy$), catchError(() => of([])))
        .subscribe((users: any) => mergeUsers(users));

      this._projectService.getFilteredTeam('manager')
        .pipe(takeUntil(this.destroy$), catchError(() => of([])))
        .subscribe((users: any) => mergeUsers(users));

      this._projectService.getFilteredTeam('lead')
        .pipe(takeUntil(this.destroy$), catchError(() => of([])))
        .subscribe((users: any) => mergeUsers(users));
    }

    // 6. Listen to employee list change events
    if (this._projectService?.EmployeeList) {
      this._projectService.EmployeeList
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this._projectService.getUserList(0, 0, true)
            .pipe(takeUntil(this.destroy$), catchError(() => of([])))
            .subscribe((users: any) => mergeUsers(users));
        });
    }
  }

  private listenToQueryParams(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        // Only read params on the very first emission (page load / deep-link).
        // After that, all fetches are triggered explicitly by user actions so we
        // avoid an infinite loop where updateDateUrlParam() → queryParams change
        // → fetchTimelogs() → updateDateUrlParam() → …
        if (this.paramInitialized) {
          return;
        }
        this.paramInitialized = true;

        if (params['startDate'] && params['endDate']) {
          const s = moment(params['startDate'], 'YYYY-MM-DD', true);
          const e = moment(params['endDate'], 'YYYY-MM-DD', true);
          if (s.isValid() && e.isValid()) {
            this.startDate = s.toDate();
            this.endDate = e.toDate();
            this.selectedPreset = this.computePresetKey(this.startDate, this.endDate);
            this.selectedFilterLabel = this.formatRangeLabel(this.startDate, this.endDate);
            this.selectedDateRange.patchValue({ start: this.startDate, end: this.endDate }, { emitEvent: false });
          }
        } else if (params['date']) {
          const parsed = moment(params['date'], 'YYYY-MM-DD', true);
          if (parsed.isValid()) {
            this.startDate = parsed.startOf('day').toDate();
            this.endDate = parsed.endOf('day').toDate();
            this.selectedPreset = this.computePresetKey(this.startDate, this.endDate);
            this.selectedFilterLabel = this.formatRangeLabel(this.startDate, this.endDate);
            this.selectedDateRange.patchValue({ start: this.startDate, end: this.endDate }, { emitEvent: false });
          }
        }

        if (params['projectId']) {
          this.selectedProjectId = String(params['projectId']);
        }

        if (params['projectName']) {
          this.filterProjectName = String(params['projectName']);
        }

        if (params['employeeId']) {
          this.selectedEmployeeId = String(params['employeeId']);
        }

        this.fetchTimelogs();
      });
  }

  private updateDateUrlParam(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        startDate: moment(this.startDate).format('YYYY-MM-DD'),
        endDate: moment(this.endDate).format('YYYY-MM-DD'),
        date: moment(this.startDate).format('YYYY-MM-DD')
      },
      queryParamsHandling: 'merge'
    });
  }

  private fetchFallbackLogs(companyId: any, startDate: string, endDate: string): void {
    this._projectService.getSavedConductTimelogs(companyId, null, null, startDate, endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (savedRes: any) => {
          this.loading = false;
          const savedLogs = savedRes?.timelogs || savedRes?.data || (Array.isArray(savedRes) ? savedRes : []);
          this.originalRawLogs = savedLogs;
          this.processIncomingLogs(savedLogs);
        },
        error: () => {
          this.loading = false;
          this.originalRawLogs = [];
          this.processIncomingLogs([]);
        }
      });
  }

  private processIncomingLogs(rawLogs: any[]): void {
    const mapped = (rawLogs || []).map(log => this.mapConductTimelogUser(log));
    this.rawTimelogs = mapped;

    // Extract available projects
    const projectsMap = new Map<string, string>();
    mapped.forEach(log => {
      const pid = String(log.project_id || log.conduct_project_id || log.project_name || 'unassigned');
      const pname = log.project_name || 'General Tasks';
      if (!projectsMap.has(pid)) {
        projectsMap.set(pid, pname);
      }
    });

    this.availableProjects = [
      { id: 'all', name: 'All Projects' },
      ...Array.from(projectsMap.entries()).map(([id, name]) => ({ id, name }))
    ];

    if (this.selectedProjectId !== 'all' && !this.filterProjectName) {
      const found = this.availableProjects.find(p => p.id === this.selectedProjectId);
      if (found) {
        this.filterProjectName = found.name;
      }
    }

    this.applyFilters();
  }

  private applyFilters(): void {
    let logs = [...this.rawTimelogs];

    // Project Filter
    if (this.selectedProjectId && this.selectedProjectId !== 'all') {
      logs = logs.filter(log => {
        const pid = String(log.project_id || log.conduct_project_id || log.project_name || 'unassigned');
        return pid === this.selectedProjectId || (this.filterProjectName && log.project_name === this.filterProjectName);
      });
    }

    // Employee Filter
    if (this.selectedEmployeeId && this.selectedEmployeeId !== '') {
      const targetEmpId = String(this.selectedEmployeeId);
      logs = logs.filter(log => {
        const uId = String(log.supersee_user_id || log.conduct_user_id || '');
        return uId === targetEmpId;
      });
    }

    // Search Query
    if (this.searchQuery && this.searchQuery.trim().length > 0) {
      const q = this.searchQuery.toLowerCase().trim();
      logs = logs.filter(log => {
        const empName = (log.employee_name || '').toLowerCase();
        const empEmail = (log.employee_email || '').toLowerCase();
        const taskTitle = (log.task_title || '').toLowerCase();
        const projName = (log.project_name || '').toLowerCase();
        return empName.includes(q) || empEmail.includes(q) || taskTitle.includes(q) || projName.includes(q);
      });
    }

    this.filteredTimelogs = logs;
    this.groupedEmployeeTimelogs = this.groupLogsByEmployee(logs);
    this.projectSummaries = this.groupLogsByProject(logs);
    this.computeKpiMetrics(logs);
    this.fetchTrackedTimeForPagedEmployees();
    this.cdr.detectChanges();
  }

  private computeKpiMetrics(logs: ConductTimelog[]): void {
    let totalConductSecs = 0;
    const usersSet = new Set<string>();
    const projectsSet = new Set<string>();

    logs.forEach(log => {
      totalConductSecs += parseInt(String(log.duration_time_second || 0), 10) || 0;
      const uKey = String(log.supersee_user_id || log.conduct_user_id || log.employee_name || '');
      if (uKey) {
        usersSet.add(uKey);
      }
      const pKey = String(log.project_id || log.project_name || 'unassigned');
      projectsSet.add(pKey);
    });

    this.totalDurationSeconds = totalConductSecs;
    this.totalTrackedHours = this.formatDurationHhMm(totalConductSecs);
    this.totalContributorsCount = usersSet.size;
    this.totalProjectsCount = projectsSet.size;
    this.totalLogsCount = logs.length;

    // Determine relevant SuperSee tracked seconds
    // Prioritize the sum of SuperSee tracked times across the employees present in the Conduct logs
    const sumGroupTracked = this.groupedEmployeeTimelogs?.reduce((acc, g) => acc + (g.supersee_tracked_seconds || 0), 0) || 0;
    const relevantSuperSeeSecs = (sumGroupTracked > 0)
      ? sumGroupTracked
      : (this.totalSuperSeeTrackedSeconds || 0);

    // 1. Card 0: Logged On Conduct (formatted as 00h 00m, no subtitle)
    const conductLoggedFormatted = this.formatDurationHhMm(totalConductSecs);
    this.conductStatsCards[0].label = 'Logged On Conduct';
    this.conductStatsCards[0].value = conductLoggedFormatted;
    this.conductStatsCards[0].subtitle = '';

    // 2. Card 1: Tracked By Supersee (formatted as 00h 00m)
    const trackedFormatted = this.formatDurationHhMm(relevantSuperSeeSecs);
    this.conductStatsCards[1].label = 'Tracked By Supersee';
    this.conductStatsCards[1].value = trackedFormatted;
    this.conductStatsCards[1].subtitle = 'Total desktop tracked time';

    // 3. Card 2: Unlogged time
    // Difference between Logged On Conduct and Tracked By Supersee
    const totalUnloggedSecs = Math.abs(relevantSuperSeeSecs - totalConductSecs);
    const unloggedFormatted = this.formatDurationHhMm(totalUnloggedSecs);
    this.conductStatsCards[2].label = 'Unlogged time';
    this.conductStatsCards[2].value = unloggedFormatted;
    this.conductStatsCards[2].subtitle = 'Difference between tracked and logged time';

    // 4. Card 3: Match rate (ratio of SuperSee tracked logs and Conduct logs matchup)
    let matchRate = 0;
    if (relevantSuperSeeSecs > 0 && totalConductSecs > 0) {
      matchRate = Math.min(
        100,
        Math.round((Math.min(totalConductSecs, relevantSuperSeeSecs) / Math.max(totalConductSecs, relevantSuperSeeSecs)) * 100)
      );
    } else {
      matchRate = 0;
    }

    this.conductStatsCards[3].label = 'Match rate';
    this.conductStatsCards[3].value = `${matchRate}%`;
    this.conductStatsCards[3].subtitle = 'Logged time matched to tracked activity';
    this.conductStatsCards[3].progress = matchRate;
    this.conductStatsCards[3].progressColor = matchRate >= 80 ? 'bg-emerald-500' : (matchRate >= 50 ? 'bg-amber-500' : 'bg-rose-500');

    // 5. Card 4: Active project
    this.conductStatsCards[4].label = 'Active project';
    this.conductStatsCards[4].value = String(this.totalProjectsCount);
    this.conductStatsCards[4].subtitle = this.totalProjectsCount === 1
      ? '1 project with logged time'
      : `${this.totalProjectsCount} projects with logged time`;
  }

  private groupLogsByEmployee(logs: ConductTimelog[]): ConductTimelogGroup[] {
    const groupsMap = new Map<string, ConductTimelogGroup>();

    logs.forEach(log => {
      const key = String(log.supersee_user_id || log.conduct_user_id || log.employee_name || 'unmapped');

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          user_key: key,
          supersee_user_id: log.supersee_user_id,
          conduct_user_id: log.conduct_user_id,
          employee_name: log.employee_name || 'Unmapped User',
          employee_email: log.employee_email || '',
          total_duration_seconds: 0,
          total_worked_hours: '00:00:00',
          expanded: true,
          logs: []
        });
      }

      const group = groupsMap.get(key)!;
      group.logs.push(log);
      const durSecs = parseInt(String(log.duration_time_second || 0), 10) || 0;
      group.total_duration_seconds += durSecs;
    });

    const result = Array.from(groupsMap.values());
    result.forEach(group => {
      // If group employee_name is still a placeholder, attempt resolution with full userList and email
      if (this.isPlaceholderName(group.employee_name)) {
        const resolved = this.resolveEmployeeDetails(
          group.supersee_user_id,
          group.employee_email,
          group.employee_name
        );
        group.employee_name = resolved.resolvedName;
        group.employee_email = resolved.resolvedEmail;
        if (!group.supersee_user_id && resolved.resolvedUserId) {
          group.supersee_user_id = resolved.resolvedUserId;
        }
      }

      const totalSecs = group.total_duration_seconds;
      const h = Math.floor(totalSecs / 3600);
      const m = Math.floor((totalSecs % 3600) / 60);
      const s = totalSecs % 60;
      group.total_worked_hours = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;

      // 1. Projects aggregation & sorting by duration descending
      const pMap = new Map<string, { id: string; name: string; duration_seconds: number }>();
      group.logs.forEach(log => {
        const pId = String(log.project_id || log.conduct_project_id || log.project_name || 'unassigned');
        const pName = log.project_name || 'General Tasks';
        const logDur = parseInt(String(log.duration_time_second || 0), 10) || 0;
        if (!pMap.has(pId)) {
          pMap.set(pId, { id: pId, name: pName, duration_seconds: 0 });
        }
        pMap.get(pId)!.duration_seconds += logDur;
      });

      const sortedProjects = Array.from(pMap.values()).sort((a, b) => b.duration_seconds - a.duration_seconds);
      group.projects = sortedProjects.map((p, idx) => ({
        ...p,
        palette: PROJECT_PALETTES[idx % PROJECT_PALETTES.length]
      }));

      group.primary_project_name = sortedProjects[0]?.name || 'General Tasks';
      group.other_projects_count = Math.max(0, sortedProjects.length - 1);
      group.all_projects_label = sortedProjects.map(p => p.name).join(', ');

      // 2. Latest Task Title (last entry or latest task)
      const lastLog = group.logs[group.logs.length - 1];
      group.latest_task_title = (lastLog?.task_title || lastLog?.task_unique_id || 'General Task').trim();

      // 3. Work Window (Earliest start time to Latest end time)
      group.work_window = this.computeWorkWindow(group.logs);

      // 4. SuperSee tracked time & unlogged time
      const uKey = String(group.supersee_user_id || group.conduct_user_id || group.user_key || '');
      const nameKey = (group.employee_name || '').toLowerCase().trim();
      const emailKey = (group.employee_email || '').toLowerCase().trim();

      let logTracked = 0;
      group.logs.forEach(log => {
        if ((log as any).supersee_tracked_seconds) {
          logTracked += parseInt(String((log as any).supersee_tracked_seconds), 10) || 0;
        } else if ((log as any).tracked_seconds) {
          logTracked += parseInt(String((log as any).tracked_seconds), 10) || 0;
        }
      });

      const trackedSecs = this.employeeTrackedSecondsMap.get(uKey)
        ?? (group.supersee_user_id ? this.employeeTrackedSecondsMap.get(String(group.supersee_user_id)) : undefined)
        ?? (nameKey ? this.employeeTrackedSecondsMap.get(nameKey) : undefined)
        ?? (emailKey ? this.employeeTrackedSecondsMap.get(emailKey) : undefined)
        ?? logTracked
        ?? 0;

      group.supersee_tracked_seconds = trackedSecs;
      group.supersee_tracked_hours = trackedSecs > 0 ? this.formatDurationHhMm(trackedSecs) : '--';
      const unlogged = Math.abs(totalSecs - trackedSecs);
      group.unlogged_seconds = unlogged;
      group.unlogged_hours = this.formatDurationHhMm(unlogged);
    });

    // Sort by unlogged time high to low, tie-broken by total duration descending
    result.sort((a, b) => {
      const unloggedA = a.unlogged_seconds || 0;
      const unloggedB = b.unlogged_seconds || 0;
      if (unloggedA !== unloggedB) {
        return unloggedB - unloggedA;
      }
      return b.total_duration_seconds - a.total_duration_seconds;
    });

    return result;
  }

  private enrichEmployeeTrackedTimes(): void {
    if (!this.groupedEmployeeTimelogs || this.groupedEmployeeTimelogs.length === 0) {
      return;
    }
    this.groupedEmployeeTimelogs.forEach(group => {
      const uKey = String(group.supersee_user_id || group.conduct_user_id || group.user_key || '');
      const nameKey = (group.employee_name || '').toLowerCase().trim();
      const emailKey = (group.employee_email || '').toLowerCase().trim();

      const trackedSecs = this.employeeTrackedSecondsMap.get(uKey)
        ?? (group.supersee_user_id ? this.employeeTrackedSecondsMap.get(String(group.supersee_user_id)) : undefined)
        ?? (nameKey ? this.employeeTrackedSecondsMap.get(nameKey) : undefined)
        ?? (emailKey ? this.employeeTrackedSecondsMap.get(emailKey) : undefined)
        ?? group.supersee_tracked_seconds
        ?? 0;

      if (trackedSecs > 0) {
        group.supersee_tracked_seconds = trackedSecs;
        group.supersee_tracked_hours = this.formatDurationHhMm(trackedSecs);
        const unlogged = Math.abs(group.total_duration_seconds - trackedSecs);
        group.unlogged_seconds = unlogged;
        group.unlogged_hours = this.formatDurationHhMm(unlogged);
      }
    });

    const totalRelevantTracked = this.groupedEmployeeTimelogs.reduce(
      (acc, g) => acc + (g.supersee_tracked_seconds || 0),
      0
    );
    if (totalRelevantTracked > 0) {
      this.totalSuperSeeTrackedSeconds = totalRelevantTracked;
    }

    this.groupedEmployeeTimelogs.sort((a, b) => {
      const unloggedA = a.unlogged_seconds || 0;
      const unloggedB = b.unlogged_seconds || 0;
      if (unloggedA !== unloggedB) {
        return unloggedB - unloggedA;
      }
      return b.total_duration_seconds - a.total_duration_seconds;
    });
    this.computeKpiMetrics(this.filteredTimelogs);
  }

  private enrichSingleGroupTrackedTime(group: ConductTimelogGroup, trackedSecs: number): void {
    if (!group) return;
    group.supersee_tracked_seconds = trackedSecs;
    group.supersee_tracked_hours = trackedSecs > 0 ? this.formatDurationHhMm(trackedSecs) : '--';
    const unlogged = Math.abs(group.total_duration_seconds - trackedSecs);
    group.unlogged_seconds = unlogged;
    group.unlogged_hours = this.formatDurationHhMm(unlogged);

    const totalRelevantTracked = this.groupedEmployeeTimelogs?.reduce(
      (acc, g) => acc + (g.supersee_tracked_seconds || 0),
      0
    ) || 0;
    if (totalRelevantTracked > 0) {
      this.totalSuperSeeTrackedSeconds = totalRelevantTracked;
    }
    this.computeKpiMetrics(this.filteredTimelogs);
  }

  fetchTrackedTimeForPagedEmployees(): void {
    if (!this.pagedEmployeeTimelogs || this.pagedEmployeeTimelogs.length === 0) {
      return;
    }
    const formattedStartDate = moment(this.startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(this.endDate).format('YYYY-MM-DD');

    this.pagedEmployeeTimelogs.forEach(group => {
      // Resolve supersee_user_id if not present
      if (!group.supersee_user_id && this.userList && this.userList.length > 0) {
        const empEmail = (group.employee_email || '').toLowerCase().trim();
        const empName = (group.employee_name || '').toLowerCase().trim();
        if (empEmail) {
          const found = this.userList.find((u: any) => (u.user_email || u.email || '').toLowerCase().trim() === empEmail);
          if (found) {
            group.supersee_user_id = found.id || found.ID || found.userId;
          }
        }
        if (!group.supersee_user_id && empName) {
          const found = this.userList.find((u: any) => {
            const uName = (u.display_name || u.name || `${u.first_name || ''} ${u.last_name || ''}`).toLowerCase().trim();
            return uName === empName;
          });
          if (found) {
            group.supersee_user_id = found.id || found.ID || found.userId;
          }
        }
      }

      const userId = group.supersee_user_id;
      const uKey = String(userId || group.conduct_user_id || group.user_key || '');
      const nameKey = (group.employee_name || '').toLowerCase().trim();
      const emailKey = (group.employee_email || '').toLowerCase().trim();

      const existingTracked = this.employeeTrackedSecondsMap.get(uKey)
        ?? (userId ? this.employeeTrackedSecondsMap.get(String(userId)) : undefined)
        ?? (nameKey ? this.employeeTrackedSecondsMap.get(nameKey) : undefined)
        ?? (emailKey ? this.employeeTrackedSecondsMap.get(emailKey) : undefined);

      if (existingTracked !== undefined && existingTracked > 0) {
        this.enrichSingleGroupTrackedTime(group, existingTracked);
      } else if (userId && this._projectService?.getWorkinHour) {
        this._projectService.getWorkinHour(formattedStartDate, formattedEndDate, String(userId))
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (workRes: any) => {
              let totalSecs = 0;
              if (workRes && typeof workRes === 'object') {
                Object.keys(workRes).forEach(k => {
                  const dayObj = workRes[k];
                  if (dayObj && typeof dayObj === 'object') {
                    totalSecs += this.parseHmsToSeconds(dayObj.productive) + this.parseHmsToSeconds(dayObj.idle);
                  }
                });
              }
              if (totalSecs > 0) {
                this.employeeTrackedSecondsMap.set(String(userId), totalSecs);
                this.employeeTrackedSecondsMap.set(uKey, totalSecs);
                if (nameKey) {
                  this.employeeTrackedSecondsMap.set(nameKey, totalSecs);
                }
                this.enrichSingleGroupTrackedTime(group, totalSecs);
                this.cdr.detectChanges();
              } else if (this._projectService?.getUserApplicationLogs) {
                this.fetchUserAppLogs(group, String(userId), formattedStartDate, formattedEndDate, uKey, nameKey);
              }
            },
            error: () => {
              if (this._projectService?.getUserApplicationLogs) {
                this.fetchUserAppLogs(group, String(userId), formattedStartDate, formattedEndDate, uKey, nameKey);
              }
            }
          });
      } else if (userId && this._projectService?.getUserApplicationLogs) {
        this.fetchUserAppLogs(group, String(userId), formattedStartDate, formattedEndDate, uKey, nameKey);
      }
    });
  }

  private fetchUserAppLogs(
    group: ConductTimelogGroup,
    userId: string,
    formattedStartDate: string,
    formattedEndDate: string,
    uKey: string,
    nameKey: string
  ): void {
    this._projectService.getUserApplicationLogs(userId, formattedStartDate, formattedEndDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (appLogsRes: any) => {
          const totalSecs = this.calculateTotalSecondsFromAppLogs(appLogsRes);
          if (totalSecs > 0) {
            this.employeeTrackedSecondsMap.set(userId, totalSecs);
            this.employeeTrackedSecondsMap.set(uKey, totalSecs);
            if (nameKey) {
              this.employeeTrackedSecondsMap.set(nameKey, totalSecs);
            }
            this.enrichSingleGroupTrackedTime(group, totalSecs);
            this.cdr.detectChanges();
          }
        },
        error: (err: any) => {
          console.warn('[ConductTimelogs] Could not fetch user app logs for user:', userId, err);
        }
      });
  }

  private calculateTotalSecondsFromAppLogs(res: any): number {
    if (!res) return 0;
    let totalSecs = 0;

    if (res.total_time?.seconds) {
      return parseInt(String(res.total_time.seconds), 10) || 0;
    }

    const keys = Object.keys(res);
    for (const key of keys) {
      if (key === 'error') continue;
      const val = res[key];
      const sessions = Array.isArray(val?.sessions)
        ? val.sessions
        : (Array.isArray(val) ? val : (Array.isArray(res?.sessions) ? res.sessions : []));

      if (Array.isArray(sessions)) {
        sessions.forEach((sess: any) => {
          if (!sess) return;
          if (sess.usage && typeof sess.usage === 'string') {
            totalSecs += this.parseHmsToSeconds(sess.usage);
            if (sess.idle && typeof sess.idle === 'string') {
              totalSecs += this.parseHmsToSeconds(sess.idle);
            }
          } else {
            const inTime = sess.inTime ?? sess.in;
            const outTime = sess.outTime ?? sess.out;
            if (inTime && outTime) {
              const startMin = this.parseTimeToMinutes(inTime);
              const endMin = this.parseTimeToMinutes(outTime);
              if (endMin > startMin) {
                totalSecs += (endMin - startMin) * 60;
              }
            }
          }
        });
      }
    }
    return totalSecs;
  }

  private computeWorkWindow(logs: ConductTimelog[]): string {
    let minStartMin: number | null = null;
    let maxEndMin: number | null = null;

    logs.forEach(log => {
      if (log.from_time) {
        const sMin = this.parseTimeToMinutes(log.from_time);
        if (sMin > 0) {
          if (minStartMin === null || sMin < minStartMin) {
            minStartMin = sMin;
          }
        }
      }

      if (log.end_time) {
        const eMin = this.parseTimeToMinutes(log.end_time);
        if (eMin > 0) {
          if (maxEndMin === null || eMin > maxEndMin) {
            maxEndMin = eMin;
          }
        }
      } else if (log.from_time && log.duration_time_second) {
        const sMin = this.parseTimeToMinutes(log.from_time);
        const durMin = Math.round((parseInt(String(log.duration_time_second), 10) || 0) / 60);
        const calcEnd = sMin + durMin;
        if (calcEnd > 0) {
          if (maxEndMin === null || calcEnd > maxEndMin) {
            maxEndMin = Math.min(1439, calcEnd);
          }
        }
      }
    });

    if (minStartMin !== null && maxEndMin !== null && maxEndMin >= minStartMin) {
      return `${this.minutesTo12h(minStartMin)} - ${this.minutesTo12h(maxEndMin)}`;
    } else if (minStartMin !== null) {
      return `Started ${this.minutesTo12h(minStartMin)}`;
    }

    return '--';
  }

  private parseTimeToMinutes(timeVal: unknown): number {
    if (!timeVal) return 0;
    const str = String(timeVal).trim();

    if (str.includes('T')) {
      const m = moment(str);
      if (m.isValid()) {
        return m.hours() * 60 + m.minutes();
      }
    }

    if (str.includes(' ') && (str.toUpperCase().includes('AM') || str.toUpperCase().includes('PM'))) {
      const m = moment(str, ['hh:mm:ss A', 'hh:mm A', 'h:mm A', 'h:mm:ss A']);
      if (m.isValid()) {
        return m.hours() * 60 + m.minutes();
      }
    }

    if (str.includes(' ')) {
      const spaceParts = str.split(' ');
      const timePart = spaceParts[1] || spaceParts[0];
      const colonParts = timePart.split(':');
      if (colonParts.length >= 2) {
        const h = parseInt(colonParts[0], 10) || 0;
        const m = parseInt(colonParts[1], 10) || 0;
        return h * 60 + m;
      }
    }

    const colonParts = str.split(':');
    if (colonParts.length >= 2) {
      const h = parseInt(colonParts[0], 10) || 0;
      const m = parseInt(colonParts[1], 10) || 0;
      return h * 60 + m;
    }

    return 0;
  }

  private minutesTo12h(minutes: number): string {
    const clamped = Math.max(0, Math.min(1439, Math.floor(minutes || 0)));
    let hours = Math.floor(clamped / 60);
    const mins = clamped % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')} ${period}`;
  }

  private groupLogsByProject(logs: ConductTimelog[]): ConductProjectSummary[] {
    const projectsMap = new Map<string, any>();

    logs.forEach(log => {
      const projId = String(log.project_id || log.conduct_project_id || log.project_name || 'unassigned');
      const projName = log.project_name || 'General Tasks';

      if (!projectsMap.has(projId)) {
        projectsMap.set(projId, {
          project_id: projId,
          project_name: projName,
          total_duration_seconds: 0,
          total_worked_hours: '0h 00m',
          total_tasks_count: 0,
          tasksSet: new Set<string>(),
          contributorsMap: new Map<string, { name: string; email: string }>(),
          contributors: []
        });
      }

      const pGroup = projectsMap.get(projId);
      const durSecs = parseInt(String(log.duration_time_second || 0), 10) || 0;
      pGroup.total_duration_seconds += durSecs;

      const taskKey = String(log.task_unique_id || log.task_id || log.task_title || '');
      if (taskKey) {
        pGroup.tasksSet.add(taskKey);
      } else {
        pGroup.total_tasks_count += 1;
      }

      const userKey = String(log.supersee_user_id || log.conduct_user_id || log.employee_name || 'unmapped');
      if (!pGroup.contributorsMap.has(userKey)) {
        pGroup.contributorsMap.set(userKey, {
          name: log.employee_name || 'User',
          email: log.employee_email || ''
        });
      }
    });

    const result = Array.from(projectsMap.values());
    result.forEach((p, idx) => {
      const totalSecs = p.total_duration_seconds;
      const h = Math.floor(totalSecs / 3600);
      const m = Math.floor((totalSecs % 3600) / 60);
      p.total_worked_hours = `${h}h ${String(m).padStart(2, '0')}m`;
      p.total_tasks_count = p.tasksSet.size > 0 ? p.tasksSet.size : p.total_tasks_count || 1;
      p.contributors = Array.from(p.contributorsMap.values());
      p.colorPalette = PROJECT_PALETTES[idx % PROJECT_PALETTES.length];
    });

    return result;
  }

  private isPlaceholderName(name?: string | null): boolean {
    if (!name) return true;
    const trimmed = String(name).trim();
    if (!trimmed) return true;
    return (
      trimmed.startsWith('SuperSee User #') ||
      trimmed.startsWith('Conduct User') ||
      trimmed.startsWith('User #') ||
      trimmed === 'Unmapped User'
    );
  }

  private formatNameFromEmail(email: string): string {
    if (!email || !email.includes('@')) {
      return '';
    }
    const localPart = email.split('@')[0];
    if (!localPart) return '';

    const parts = localPart
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[._-]+/g, ' ')
      .replace(/\d+/g, '')
      .trim()
      .split(/\s+/);

    const capitalized = parts
      .filter(p => p.length > 0)
      .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
      .join(' ');

    return capitalized || localPart;
  }

  private resolveEmployeeDetails(
    userId: any,
    empEmail: string,
    empName: string
  ): { resolvedName: string; resolvedEmail: string; resolvedUserId: any } {
    const emailToMatch = (empEmail || '').toLowerCase().trim();
    const strUserId = userId !== undefined && userId !== null ? String(userId).trim() : '';
    const nameToMatch = (empName || '').toLowerCase().trim();

    let matchedUser: any = null;

    // 1. Look in userList by ID or Email
    if (this.userList && this.userList.length > 0) {
      matchedUser = this.userList.find((u: any) => {
        if (!u) return false;
        const uId = String(u.id ?? u.ID ?? u.userId ?? '').trim();
        if (strUserId && uId && uId === strUserId) {
          return true;
        }
        const uEmail = (u.user_email || u.email || '').toLowerCase().trim();
        if (emailToMatch && uEmail && uEmail === emailToMatch) {
          return true;
        }
        return false;
      });

      // Secondary match by name if name is not placeholder
      if (!matchedUser && nameToMatch && !this.isPlaceholderName(nameToMatch)) {
        matchedUser = this.userList.find((u: any) => {
          const uName = (u.display_name || u.name || `${u.first_name || ''} ${u.last_name || ''}`).toLowerCase().trim();
          return uName && uName === nameToMatch;
        });
      }
    }

    // 2. Look in loggedInUser / storedUser
    if (!matchedUser) {
      const loggedInUser = this.tokenEncryptionService?.getEncryptedItem<any>('logedInUser') || {};
      const storedUser = this.tokenEncryptionService?.getEncryptedItem<any>('user') || {};
      const candidates = [loggedInUser, storedUser];

      for (const cand of candidates) {
        if (cand && (cand.id || cand.ID || cand.userId)) {
          const candId = String(cand.id ?? cand.ID ?? cand.userId ?? '').trim();
          const candEmail = (cand.user_email || cand.email || '').toLowerCase().trim();
          if ((strUserId && candId === strUserId) || (emailToMatch && candEmail === emailToMatch)) {
            matchedUser = cand;
            break;
          }
        }
      }
    }

    let finalName = empName;
    let finalEmail = empEmail;
    let finalUserId = userId;

    if (matchedUser) {
      finalUserId = finalUserId || matchedUser.id || matchedUser.ID || matchedUser.userId;
      finalEmail = finalEmail || matchedUser.user_email || matchedUser.email || '';

      const uName = (
        matchedUser.display_name ||
        matchedUser.name ||
        (matchedUser.first_name ? `${matchedUser.first_name} ${matchedUser.last_name || ''}`.trim() : '') ||
        matchedUser.user_nicename
      );
      if (uName && !this.isPlaceholderName(uName)) {
        finalName = uName;
      }
    }

    // If finalName is still placeholder or empty, but email exists, format from email
    if (this.isPlaceholderName(finalName)) {
      if (finalEmail) {
        const fromEmail = this.formatNameFromEmail(finalEmail);
        if (fromEmail) {
          finalName = fromEmail;
        }
      }
    }

    // Fallback if still empty
    if (!finalName || this.isPlaceholderName(finalName)) {
      if (finalUserId) {
        finalName = `SuperSee User #${finalUserId}`;
      } else {
        finalName = 'Unmapped User';
      }
    }

    return {
      resolvedName: finalName,
      resolvedEmail: finalEmail,
      resolvedUserId: finalUserId
    };
  }

  private mapConductTimelogUser(log: any): ConductTimelog {
    const rawUserId = log.supersee_user_id || log.supersee_user;
    const rawEmpEmail = log.employee_email || log.user_email || log.email || '';
    const rawEmpName = log.original_employee_name ?? log.employee_name ?? '';

    const { resolvedName, resolvedEmail, resolvedUserId } = this.resolveEmployeeDetails(
      rawUserId,
      rawEmpEmail,
      rawEmpName
    );

    let workedHours = log.worked_hours || '';
    if (!workedHours && log.duration_time_second !== undefined && log.duration_time_second !== null) {
      const totalSecs = parseInt(String(log.duration_time_second), 10) || 0;
      const h = Math.floor(totalSecs / 3600);
      const m = Math.floor((totalSecs % 3600) / 60);
      const s = totalSecs % 60;
      workedHours = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }

    return {
      ...log,
      original_employee_name: log.original_employee_name ?? log.employee_name,
      supersee_user_id: resolvedUserId,
      employee_name: resolvedName,
      employee_email: resolvedEmail,
      worked_hours: workedHours,
      date: log.date || log.log_date || (log.from_time && String(log.from_time).includes('T') ? moment(log.from_time).format('YYYY-MM-DD') : '')
    };
  }
}
